import PropTypes from "prop-types";
import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { LEAD_SOURCES } from "@tomocredit/common-fe/consts";
import SignupConfirmationModal from "./SignupConfirmationModal";
import Fade from "./Fade";
import { track, identify } from "./analytics";
import {
  fromWebview,
  utmParams,
  referralParam,
  totalUriParams,
  isMobile,
  currMobileBrowserAccepted,
  isFinicityEmail,
  isTomoEmail,
} from "../utils/userAgentInfo";

const Email = ({
  submitButtonStyle,
  errorTextClassName,
  bounce,
  showTooltip,
  setShowTooltip = () => {},
  dark,
  text = "Apply Now!",
}) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalState, setModalState] = useState("");
  const [waitlistPosition, setWaitlistPosition] = useState(null);
  const [referralCode, setReferralCode] = useState(null);
  const [placeholderText, setPlaceholderText] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const inputEl = useRef(null);

  const sendOldEmailVerification = !!((isMobile() && (utmParams.source || !currMobileBrowserAccepted())) || fromWebview);

  useEffect(() => {
    if (showTooltip) {
      let i = 0;
      const txt = "Type your email here!";
      const speed = 50;

      const typeWriter = () => {
        if (i < txt.length) {
          setPlaceholderText(txt.substr(0, i));
          i++;
          setTimeout(typeWriter, speed);
        }
      };

      typeWriter();
    }
  }, [showTooltip]);

  const isValidEmail = () => {
    if (!email) {
      setError("Email address is required");
      setShowConfirmation(false);
      return false;
    } if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]+$/i.test(email)) {
      setError("Invalid email address");
      setShowConfirmation(false);
      return false;
    }
    setError(null);
    return true;
  };

  const closeModal = () => {
    setModalState("");
    setEmail("");
  };

  const getUser = async () => {
    console.log("getting user");
    return await axios
      .post("/users/getUser", { email, fromLanding: true })
      .then((response) => {
        const { referral_code } = response.data;
        setReferralCode(referral_code);
        return response.data;
      })
      .catch((error) => {
        setError("Something went wrong!");
      });
  };

  const createUser = async () => {
    const { location } = window;

    if (!sendOldEmailVerification && isTomoEmail(email)) {
      location.replace(`${process.env.REACT_APP_DOMAIN}/register/basicinfo${totalUriParams || '?'}&user=${email}`);
      return;
    }

    // if(!sendOldEmailVerification || isFinicityEmail(email)) {
    //   location.replace(`${process.env.REACT_APP_DOMAIN}/register/basicinfo${totalUriParams || '?'}&user=${email}`);
    //   return;
    // }

    // user is in webview so go through old email confirmation route
    const userPayload = {
      withReferralCode: referralParam,
      sendOldEmailVerification,
      userId: email,
      utm: utmParams,
      source: LEAD_SOURCES.CARD_LANDING,
    };

    const defaultErrorMessage = "We are experiencing server issues. Please try again later.";
    const invalidEmailErrorMessage = "Please enter a valid email";

    let newDBUserResult;
    try {
      newDBUserResult = await axios.post("/users/newUserFromLanding", userPayload);
    } catch (err) {
      const { status } = err.response || {};
      const { message } = err.response?.data || {};
      const errorMessage = (status === 400 && message === "Invalid email") ? invalidEmailErrorMessage : defaultErrorMessage;
      setError(errorMessage);
      setLoading(false);
      return false;
    }
    const { referral_code } = newDBUserResult.data;
    setShowConfirmation(true);
    setReferralCode(referral_code);
    setLoading(false);
    return true;
  };

  const handleSubmit = async () => {
    if (!isValidEmail()) {
      // window.heap.addUserProperties({ lastEmailAttempted: email });
      return null;
    }

    identify(email, {
      email,
    });
    track("JOIN_WAITLIST_TOP_CLICKED");

    if (typeof window !== "undefined") {
      // window.heap.identify(email);
      if (window._tfa != null) {
        window._tfa.push({
          notify: "event",
          name: "Lead",
        });
      }
      if (window.obApi != null) {
        window.obApi("track", "Lead");
      }
      if (window.dotq != null) {
        window.dotq.push({
          projectId: "10000",
          properties: {
            pixelId: "10171353",
            qstrings: {
              et: "custom",
              ec: "button",
              ea: "submit",
              el: "Application Submission",
            },
          },
        });
      }
      if (window.snaptr != null) {
        window.snaptr("track", "Lead");
      }
      window.dataLayer?.push({
        event: "HomeLead",
        email,
      });
    }

    setLoading(true);
    const data = await getUser();
    setLoading(false);
    const { login, applied, resume, signup } = data;
    setError(null);

    if (login) {
      setModalState("login");

    } else if (resume) {
      setModalState("resume");
    } else if (applied) {
      setModalState("applied");
    } else if (signup) {
      const userCreated = await createUser();
      if (!isFinicityEmail(email) && userCreated) {
        setModalState("signup");
      }
    } else {
      setShowConfirmation(false);
      setError("Could not communicate with server. Please try again later");
    }
  };

  return (
    <div className="">
      <div className="justify-center w-full flex flex-col lg:flex-row rounded-lg">
        <div className="flex-1 inline-block flex-grow w-full mb-3 lg:mb-0">
          <input
            style={{ WebkitUserSelect: "auto", paddingRight: 0, minWidth: 300 }}
            className={`placeholder-gray-600 text-gray-600 rounded-lg lg:rounded-r-none p-4 w-full outline-none bg-white text-xl lg:border-r-0 bg-transparent ${
              dark
                ? "placeholder-primary-txt text-white border-primary-btn"
                : "border-gray-800"
            }`}
            placeholder={
              placeholderText !== "" ? placeholderText : "Enter your email"
            }
            disabled={loading}
            onChange={(e) => {
              setEmail((e.target.value.trim() || "").toLowerCase());
              setShowConfirmation(false);
            }}
            onFocus={() => {
              setError(null);
              setShowConfirmation(false);
              setShowTooltip(false);
            }}
            value={email}
            ref={inputEl}
            onKeyUp={(e) => {
              const keyCode = e.keyCode || e.which;
              if (keyCode == "13") {
                handleSubmit();
                return false;
              }
            }}
          />
        </div>

        <button
          type="button"
          className={`gtm-apply-now btn cursor-pointer rounded-lg lg:rounded-l-none text-center font-body px-6 py-4 font-bold text-xl bg-primary-btn grow-0 shadow-2xl w-full lg:w-auto hover:bg-greenHover ${
            bounce ? "presignupButton" : ""
          }`}
          style={{
            ...submitButtonStyle,
            outline: "none",
            minWidth: 175,
            boxShadow: "rgb(86 45 100 / 15%) 13px 11px 36px -10px",
          }}
          onClick={handleSubmit}
          disabled={loading}
        >
          <Fade show={loading} className="h-0">
            <i
              style={{ fontSize: 28 }}
              className="fa fa-pulse fa-spinner px-6 fadeIn"
            />
          </Fade>
          <Fade show={!loading} hideOnly>
            <span
              className={`fadein ${dark ? "text-primary-dark" : "text-dark"}`}
            >
              {text}
            </span>
          </Fade>
        </button>
      </div>
      {error && (
        <p
          className={`text-sm text-white text-left ml-2 mt-2 absolute ${
            errorTextClassName || ""
          }`}
        >
          {error}
        </p>
      )}
      <div
        style={{
          transition: "all 0.5s ease",
          top: showConfirmation ? 0 : -10,
        }}
        className={`text-center sm:text-left font-open-sans mt-3 text-black relative text-sm ${
          showConfirmation ? "opacity-100" : "opacity-0"
        }`}
      >
        <i className="fa fa-check-circle mr-2" />
        <span>Success! Check your email for more details.</span>
      </div>

      {modalState && (
        <SignupConfirmationModal
          email={email}
          closeModal={closeModal}
          modalState={modalState}
          waitlistPosition={waitlistPosition}
          referralCode={referralCode}
        />
      )}
    </div>
  );
};

Email.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,
  submitButtonClassName: PropTypes.string,
  submitButtonStyle: PropTypes.object,
  onSubmit: PropTypes.func,
};

Email.defaultProps = {
  className: ``,
  style: {},
  submitButtonClassName: ``,
  submitButtonStyle: {},
  onSubmit: () => {},
};

export default Email;
